import React, { useMemo, useState, useEffect } from "react";
import Button from "../../../../components/Button.jsx";
import Table from "../../../../components/Table.jsx";
import Pagination from "../../../../components/Pagination.jsx";
import Navbar from "../../../../components/navbar.jsx";
import Footer from "../../../../components/footer.jsx";
import styles from "../css/clinic-services.module.css";
import AddEditServiceModal from "../components/AddEditServiceModal.jsx";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal.jsx";
import ErrorModal from "../components/ErrorModal.jsx";
import { listServices, createService, updateService, deleteService } from "../../../../api/clinic/servicesApi.js";

export default function ClinicServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  const filteredServices = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return services;

    return services.filter((svc) => {
      const code = String(svc?.servicecode ?? "").toLowerCase();
      const name = String(svc?.servicename ?? "").toLowerCase();
      return code.includes(term) || name.includes(term);
    });
  }, [services, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredServices.length / pageSize));

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const pageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredServices.slice(start, start + pageSize);
  }, [filteredServices, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  // Fetch services from the backend
  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listServices();
      setServices(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[ERROR] Failed to fetch services:', err);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (svc) => {
    try {
      setStatusUpdatingId(svc.serviceid);
      await updateService(svc.serviceid, { status: !svc.status });
      setServices((prev) => prev.map((s) => s.serviceid === svc.serviceid ? { ...s, status: !svc.status } : s));
    } catch (err) {
      console.error('[ERROR] Failed to update service status:', err);
      const message = err.response?.data?.message || err.message || 'Failed to update service status';
      setErrorMessage(message);
      setErrorModalOpen(true);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const columns = [
    { header: "Service Code", accessor: "servicecode" },
    { header: "Service Name", accessor: "servicename" },
    { header: "Price (R)", accessor: "price", render: (val) => Number(val).toFixed(2) },
    { header: "Duration (Min)", accessor: "durationinmin" },
    {
      header: "Action",
      accessor: "action",
      render: (_, row) => (
        <div className={styles.actionBtns}>
          <Button
            variant="primary"
            onClick={() => {
              setEditing(row);
              setModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            variant={row.status ? "secondary" : "approve"}
            className={row.status ? styles.deleteBtn : ''}
            onClick={() => toggleStatus(row)}
            disabled={statusUpdatingId === row.serviceid}
            style={!row.status ? undefined : undefined}
          >
            {row.status ? 'Disable' : 'Enable'}
          </Button>
        </div>
      ),
    },
  ];

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const handleSave = async (data) => {
    try {
      if (data.serviceid) {
        // Update existing service
        await updateService(data.serviceid, data);
        await fetchServices();
        setModalOpen(false);
        setEditing(null);
      } else {
        // Create new service
        await createService(data);
        await fetchServices();
        setModalOpen(false);
        setEditing(null);
      }
    } catch (err) {
      console.error('[ERROR] Failed to save service:', err);
      console.error('[ERROR] Error response:', err.response);
      console.error('[ERROR] Error data:', err.response?.data);
      
      // Extract error message from response or use default
      let message = 'Failed to save service. Please try again.';
      
      if (err.response?.data?.message) {
        message = err.response.data.message;
      } else if (err.message) {
        message = err.message;
      }
      
      setErrorMessage(message);
      setErrorModalOpen(true);
      // Keep modal open so user can correct the error
    }
  };

  const openDeleteModal = (service) => {
    setServiceToDelete(service);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!serviceToDelete) return;
    
    try {
      await deleteService(serviceToDelete.serviceid);
      await fetchServices();
      setServiceToDelete(null);
    } catch (err) {
      console.error('[ERROR] Failed to delete service:', err);
      const message = err.response?.data?.message || 'Failed to delete service. Please try again.';
      setErrorMessage(message);
      setErrorModalOpen(true);
    }
  };

  return (
    <div className={styles.page}>
      <Navbar />
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Clinic Services</h1>
          <Button className={styles.addBtn} onClick={openAdd}>Add</Button>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.searchRow}>
          <input
            className={styles.searchInput}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by service code or service name"
          />
        </div>
        <Table columns={columns} data={pageData} />
        <div className={styles.paginationRow}>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <AddEditServiceModal
        open={modalOpen}
        initialData={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        onError={(message) => {
          setErrorMessage(message);
          setErrorModalOpen(true);
        }}
      />
      
      <ConfirmDeleteModal
        open={deleteModalOpen}
        serviceName={serviceToDelete?.servicename}
        onClose={() => {
          setDeleteModalOpen(false);
          setServiceToDelete(null);
        }}
        onConfirm={handleDelete}
      />
      
      <ErrorModal
        open={errorModalOpen}
        message={errorMessage}
        onClose={() => {
          setErrorModalOpen(false);
          setErrorMessage("");
        }}
      />
      
      <Footer />
    </div>
  );
}
